// Seed discipline (the suggestion "named streams, per-concern sub-seeds, pure cell builders, stability tests"): every
// generated thing draws from a generator seeded by `subSeed(worldSeed, tag, i, j)`, a hash of the world seed, a tag
// naming the concern ("terrain:hills", "scatter:tree") and the cell's coordinates. So a cell's content never depends
// on which cells were built before it, and adding a rule or a layer never reshuffles another's. No Math.random.
//
// The hash is 32-bit FNV-1a over the tag, mixed with murmur3's finaliser; the generator is mulberry32. The world
// hash (`WorldHasher`) is a 64-bit FNV-1a over quantised numbers, as two 32-bit halves.

/** FNV-1a of a string, 32 bits. */
export function hashTag(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** murmur3's 32-bit finaliser. */
function fmix(h: number): number {
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return h >>> 0;
}

/** The seed of one concern (`tag`) in one cell (i, j) of a world: a pure function of its four inputs. */
export function subSeed(seed: number, tag: string, i = 0, j = 0): number {
  let h = fmix((seed >>> 0) ^ hashTag(tag));
  h = fmix(h ^ Math.imul(i | 0, 0x27d4eb2d));
  h = fmix(h ^ Math.imul(j | 0, 0x165667b1));
  return h;
}

/** A seeded generator: floats in [0, 1) (mulberry32). */
export interface CellRng {
  next(): number;
  /** A raw uint32. */
  u32(): number;
}

/** A generator for one concern in one cell. */
export function cellRng(seed: number, tag: string, i = 0, j = 0): CellRng {
  let s = subSeed(seed, tag, i, j) | 0;
  const u32 = (): number => {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return (t ^ (t >>> 14)) >>> 0;
  };
  return { u32, next: () => u32() / 4294967296 };
}

/** A hash over quantised values (the world hash): equal inputs give equal hex, in any JavaScript engine. */
export class WorldHasher {
  private a = 0x811c9dc5;
  private b = 0xcbf29ce4;
  private byte(v: number): void {
    this.a = Math.imul(this.a ^ (v & 0xff), 0x01000193) >>> 0;
    this.b = Math.imul((this.b ^ (v & 0xff)) + 0x9e3779b9, 0x5bd1e995) >>> 0;
  }
  /** A 32-bit integer. */
  int(v: number): this {
    const x = v | 0;
    this.byte(x);
    this.byte(x >>> 8);
    this.byte(x >>> 16);
    this.byte(x >>> 24);
    return this;
  }
  /** A number quantised to `q` (default a millimetre). */
  num(v: number, q = 1e-3): this {
    return this.int(Math.round(v / q));
  }
  str(s: string): this {
    this.int(s.length);
    for (let i = 0; i < s.length; i++) this.int(s.charCodeAt(i));
    return this;
  }
  /** Fold another digest in. */
  hex(h: string): this {
    return this.str(h);
  }
  digest(): string {
    return (this.a >>> 0).toString(16).padStart(8, "0") + (this.b >>> 0).toString(16).padStart(8, "0");
  }
}
